/**
 * What an administrator opens in the morning.
 *
 * Five counters, and they answer **one** question: *does somebody who joins
 * today arrive at something ready?* Not charts — a chart answers "how has this
 * moved", which nobody asks of an organization of nine people. And each counter
 * says what is **missing** beside what is there, because "7 workspaces" is a
 * total and "7 of 9 everybody can reach" is an answer.
 *
 * **These are the only figures in the product.** The rail counts nothing, a tab
 * counts nothing, a title carries no total — that is what left when the
 * navigation stopped decorating itself. Here the counting is the content.
 *
 * And **no verbs at all.** An overview answers a question; it is not a place
 * where anything is done. *Refresh* asked somebody to doubt the page they were
 * reading, *Invite* opened Members, and *Link a machine* opened Machines — three
 * buttons whose whole effect was to leave. What is worth doing is under
 * *Waiting for somebody*, where each row says what it is before it offers to
 * open it.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../../theme/tokens';
import { PageBody } from '../../components/list';
import { Row, RowAction, Table } from '../../components/primitives';
import { HairList, HairRow, SectionHead } from '../../components/Section';
import { DateCell } from '../../components/DateCell';
import { api, apiPath } from '../../lib/api';
import { hasBoughtSeats, seatFigures, unpaidBillingNote } from '../../lib/billing';
import { isoDay } from '../../lib/format';
import type { PageId } from '../../components/nav';
import type { Organization, OrgOverview } from '../../lib/types';
import { actorLabel, failureText } from './shared';
import { PageError, SkeletonPage, useLoadPhase } from '../../components/Loading';

const AGENT_NAME: Record<string, string> = { 'claude-code': 'Claude Code', codex: 'Codex' };

/** Latest publishes' grid: Workspace · Changed · By · When. */
const PUBLISHED_COLUMNS = 'minmax(0,1fr) minmax(0,1.5fr) minmax(0,.8fr) 120px';

/** How many paths a row names before it starts counting instead. */
const NAMED = 3;

/**
 * What one publish changed, in the words the trail kept.
 *
 * `detail.paths` are the keys of the document that moved — `repos[api].branch`,
 * `config.vmMemoryMb`, `onboarding.agentSettings` — which is the whole of what
 * this row is worth reading for. A restore says where it came from instead,
 * because "v9 from v4" is the change; nothing else about it is.
 */
function Changed({ entry }: { entry: OrgOverview['published'][number] }): React.JSX.Element {
  const paths = entry.detail.paths ?? [];
  const count = entry.detail.count ?? paths.length;

  if (entry.detail.from !== undefined) {
    return <span style={{ color: color.inkSubtle }}>brought back from v{entry.detail.from}</span>;
  }
  if (paths.length === 0) {
    return (
      <span style={{ color: color.inkFaint }}>
        {count > 0 ? `${count} change${count === 1 ? '' : 's'}` : 'first version'}
      </span>
    );
  }
  return (
    <span style={{ minWidth: 0, fontFamily: font.mono, fontSize: 11.5, color: color.inkSubtle, overflowWrap: 'anywhere' }}>
      {paths.slice(0, NAMED).join(', ')}
      {paths.length > NAMED && (
        <span style={{ color: color.inkFaint }}> +{paths.length - NAMED} more</span>
      )}
    </span>
  );
}

/** What each audit action did, in the words the row needs. */
const DID: Record<string, string> = {
  'project.create': 'published for the first time',
  'project.publish': 'a new version',
  'project.restore': 'an old version brought back',
  'settings.publish': 'the organization’s settings',
  'workflows.publish': 'the organization’s workflows'
};

export function OrgOverviewPage({
  organization,
  trialSeats,
  onGo
}: {
  organization: Organization;
  /** The trial's ceiling (`TRIAL_SEATS`, from `/api/config`), which a trial's seats are counted against. */
  trialSeats: number;
  onGo: (page: PageId, section?: string | null) => void;
}): React.JSX.Element {
  const [seen, setSeen] = useState<OrgOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setSeen(await api.get<OrgOverview>(apiPath`/orgs/${organization.id}/overview`));
  }, [organization.id]);

  useEffect(() => {
    load().catch((failure: unknown) => setError(failureText(failure, 'The overview could not be read.')));
  }, [load]);

  const phase = useLoadPhase(!seen && !error);
  if (!seen && error) {
    return (
      <PageBody>
        <PageError
          onRetry={() =>
            load()
              .then(() => setError(null))
              .catch((failure: unknown) => setError(failureText(failure, 'The overview could not be read.')))
          }
        />
      </PageBody>
    );
  }
  if (!seen || phase !== 'content') return <PageBody>{phase === 'skeleton' && <SkeletonPage />}</PageBody>;

  const { handed } = seen;
  // Each note agrees with its own figure. "1 are shared with a team" is a
  // sentence written around a number nobody expected to be 1, and it reads as
  // one — which undoes the counter, because a figure in prose that does not fit
  // its verb is a figure the reader stops believing.
  const behind = handed.workspaces.total - handed.workspaces.ready;
  const theirs = handed.settings.of - handed.settings.decided;
  const figures = seatFigures(organization, handed.seats.held, trialSeats);
  const offered = handed.agents.offered.length;
  const bare = offered - handed.agents.configured;

  const counters: { label: string; value: string; unit?: string; note: string; short?: boolean }[] = [
    {
      label: 'Workspaces waiting',
      value: String(handed.workspaces.ready),
      unit: handed.workspaces.total === handed.workspaces.ready ? 'all of them' : `of ${handed.workspaces.total}`,
      note:
        handed.workspaces.total === 0
          ? 'None published yet. Until one is, a new member links Airlock and finds an empty list.'
          : behind === 0
            ? 'Every one is shared with a team or with named people, so whoever it was handed to pulls it.'
            : `${behind} ${behind === 1 ? 'reaches' : 'reach'} only whoever published ${behind === 1 ? 'it' : 'them'} — nobody else sees ${behind === 1 ? 'it' : 'them'} until ${behind === 1 ? 'it is' : 'they are'} shared under Access.`
    },
    {
      label: 'Settings decided',
      value: String(handed.settings.decided),
      unit: `of ${handed.settings.of}`,
      note:
        handed.settings.decided === 0
          ? 'Every member keeps their own. Nothing is wrong with that — it is simply not onboarding.'
          : `${theirs === 0 ? 'Nothing is left to the member' : `${theirs} ${theirs === 1 ? 'stays' : 'stay'} each member's own`}${handed.settings.locked ? `, and ${handed.settings.locked} of yours cannot be changed on a machine` : ', and any of yours can be changed on a machine'}.`
    },
    {
      label: 'Agents offered',
      value: handed.agents.offered.map((agent) => AGENT_NAME[agent] ?? agent).join(' · ') || 'none',
      note:
        offered === 0
          ? 'None is offered, so a new install keeps whichever agents are already on the machine.'
          : bare === 0
            ? // Not "both": what is offered is a list an organization edits, and
              // a sentence that counts to two silently goes wrong at three.
              'Each carries a configuration. Signing in stays theirs — a subscription token never enters a sandbox.'
            : `${bare} of the ${offered} ${bare === 1 ? 'carries' : 'carry'} no configuration, so a session starts from the agent's own defaults.`,
      short: true
    },
    {
      label: 'Integrations on',
      value: handed.integrations.connected.join(' · ') || 'none',
      note: handed.integrations.off.length
        ? `${handed.integrations.off.join(', ')} ${handed.integrations.off.length === 1 ? 'is' : 'are'} off — a workspace naming one shows it as something to do.`
        : 'Everything this organization can connect is on.',
      short: true
    },
    {
      label: 'Seats held',
      value: String(handed.seats.held),
      // A trial and a grant buy nothing and their seats work: "none bought"
      // and "more given out than paid for, so machines are waiting" told the
      // owner of a fresh trial their members were locked out
      // (`seatFigures`, the count Members makes).
      unit: hasBoughtSeats(organization.billing)
        ? `of ${handed.seats.bought} bought`
        : figures.free === null
          ? 'granted'
          : organization.billing.plan === 'trial'
            ? `of ${trialSeats} on the trial`
            : 'none bought',
      // The note names the one thing to act on, and a member here without a
      // seat comes first: they are a colleague who cannot work, which nobody
      // finds out from a number of seats. An invitation is the quieter fact
      // now that it takes no seat and costs nothing.
      note: handed.seats.seatless
        ? `${handed.seats.seatless} member${handed.seats.seatless === 1 ? '' : 's'} here without one — Airlock unlocks on their machines when a seat is given.`
        : hasBoughtSeats(organization.billing) && handed.seats.held > handed.seats.bought
          ? 'More seats are given out than are paid for, so machines are waiting.'
          : handed.seats.invited
            ? `${handed.seats.invited} invitation${handed.seats.invited === 1 ? '' : 's'} waiting, costing nothing — accepted, each is a member to give a seat to.`
            : handed.seats.held === 0
              ? 'Nobody holds one yet. Invite people first, and give them a seat each.'
              : hasBoughtSeats(organization.billing)
                ? 'Every seat given out is paid for.'
                : organization.seatAccess === 'none'
                  ? 'The seats given out have stopped working; buying seats on Billing starts them again.'
                  : figures.free === null
                  ? 'Every seat given out works; nothing is charged while the grant holds.'
                  : `Every seat given out works; ${figures.free} more can be given before buying.`
    }
  ];

  return (
    <>
      <PageBody gap={40}>
        <section aria-labelledby="ov-handed">
          <SectionHead
            id="ov-handed"
            title="A new developer is handed"
            description="What their install takes at its first link, without them setting anything. What is not here, they do themselves."
          />
          {handed.workspaces.total === 0 ? (
            // An organization with no workspace yet (rule 21): the same tile,
            // a title and one sentence, no case — five zeros say nothing.
            <div style={{ background: color.surfaceAlt, borderRadius: radius.xxl, padding: '18px 20px' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: color.ink }}>Nothing is handed to a new developer yet</div>
              <p style={{ margin: '4px 0 0', maxWidth: 680, fontSize: 13, lineHeight: 1.5, color: color.inkMuted }}>
                The first workspace somebody publishes from their Airlock is what a new install pulls; the counts start
                then.
              </p>
            </div>
          ) : (
            <div
              className="al-quad"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, minmax(0,1fr))',
                // Five #F5F5F7 cases 8 apart — the page's one tile (§40.1).
                gap: 8
              }}
            >
              {counters.map((counter) => (
                <div
                  key={counter.label}
                  style={{ background: color.surfaceAlt, borderRadius: radius.xxl, padding: '18px 20px', minWidth: 0 }}
                >
                  <div style={{ fontSize: 12.5, color: color.inkFaint }}>{counter.label}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 6, minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: counter.short ? 17 : 28,
                        lineHeight: counter.short ? 1.3 : 1.1,
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: counter.short ? 0 : '-.02em',
                        fontWeight: 600,
                        color: color.ink,
                        // A text value wraps rather than lose its end (§40.1,
                        // two columns on a phone); a figure stays on its line.
                        overflow: counter.short ? undefined : 'hidden',
                        textOverflow: counter.short ? undefined : 'ellipsis',
                        whiteSpace: counter.short ? 'normal' : 'nowrap',
                        minWidth: 0
                      }}
                    >
                      {counter.value}
                    </span>
                    {counter.unit && <span style={{ fontSize: 13.5, fontWeight: 500, color: color.inkFaint }}>{counter.unit}</span>}
                  </div>
                  <div style={{ fontSize: 12.5, lineHeight: 1.45, color: color.inkMuted, marginTop: 8 }}>{counter.note}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* A hairline list (§40.3): a few things somebody has to do, each
            saying what it is before it offers to open it. With nothing
            waiting the list says so in the same form — no colour, it is not
            an alert. */}
        <section aria-labelledby="ov-waiting">
          <SectionHead id="ov-waiting" title="Waiting for somebody" />
          <HairList>
            {seen.waiting.length === 0 ? (
              <HairRow title="Nobody is waiting on anything." />
            ) : (
              seen.waiting.map((one) => (
                <HairRow
                  key={one.id}
                  mark="ring"
                  title={one.what}
                  description={one.why}
                  end={
                    <RowAction label={`Open: ${one.what}`} onClick={() => onGo(one.page as PageId)}>
                      Open
                    </RowAction>
                  }
                />
              ))
            )}
          </HairList>
        </section>

        <section aria-labelledby="ov-published">
          <SectionHead
            id="ov-published"
            title="Latest publishes"
            link={<RowAction onClick={() => onGo('audit')}>All of the audit</RowAction>}
          />
          {seen.published.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: color.inkFaint }}>
              Nothing has been published yet. A workspace arrives here when somebody publishes one from their Airlock.
            </p>
          ) : (
            <Table columns={PUBLISHED_COLUMNS} header={['Workspace', 'Changed', 'By', 'When']}>
              {seen.published.map((entry) => (
                <Row key={entry.id} columns={PUBLISHED_COLUMNS}>
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        color: color.ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {entry.projectName ?? organization.name}
                      {entry.version === null ? '' : ` · v${entry.version}`}
                    </span>
                    <span style={{ display: 'block', fontSize: 11.5, color: color.inkFaint, marginTop: 2 }}>
                      {DID[entry.action] ?? entry.action}
                    </span>
                  </span>
                  {/* What actually moved. A row that said only "a new version"
                      is a row nobody can act on — the one question an
                      administrator has of a publish is *what changed*, and the
                      trail has carried the answer all along. */}
                  <Changed entry={entry} />
                  <span
                    style={{ fontSize: 13, color: color.inkSubtle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {actorLabel(entry.actor)}
                  </span>
                  <DateCell at={entry.at} style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }} />
                </Row>
              ))}
            </Table>
          )}
        </section>

        {/* The billing summary (§40.4): the figure and its unit, then one
            sentence; on a trial the sentence alone — nothing is charged. No
            box (rule 20). */}
        <section aria-labelledby="ov-billing">
          <SectionHead id="ov-billing" title="Billing" />
          {seen.billing.perSeatUsd === null ? (
            <p style={{ margin: 0, maxWidth: 680, fontSize: 13, lineHeight: 1.5, color: color.inkMuted }}>
              {unpaidBillingNote(organization)}
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span
                  style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-.02em', color: color.ink, fontVariantNumeric: 'tabular-nums' }}
                >
                  {seen.billing.seats} × ${seen.billing.perSeatUsd}
                </span>
                <span style={{ fontSize: 14.5, color: color.inkMuted }}>
                  / {seen.billing.plan === 'annual' ? 'year' : 'month'} excl. VAT
                </span>
              </div>
              <p style={{ margin: '8px 0 0', maxWidth: 680, fontSize: 13, lineHeight: 1.5, color: color.inkMuted }}>
                {seen.billing.nextAt ? `Next invoice on ${isoDay(seen.billing.nextAt)}. ` : ''}
                A seat added on Billing is billed pro rata from that day; an invitation costs nothing.
              </p>
            </>
          )}
        </section>

        {error && <p style={{ fontSize: 13, color: color.badText, margin: 0 }}>{error}</p>}
      </PageBody>
    </>
  );
}
